import React, { useState, useRef, useEffect } from 'react';
import { FaSpinner } from 'react-icons/fa';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { usePlan } from '../../../contexts/PlanContext';
import { useAnalytics } from '../../../hooks/useAnalytics';
import { useRadarData } from '../../../hooks/useRadarData';
import RadarHeader from '../../../components/radar/RadarHeader';
import RadarAccounts from '../../../components/radar/RadarAccounts';
import RadarContacts from '../../../components/radar/RadarContacts';
import RadarTranscriptAnalyzer from '../../../components/radar/RadarTranscriptAnalyzer';
import RadarBuyerCards from '../../../components/radar/RadarBuyerCards';
import './Radar.css';

const Radar = () => {
  const { user } = useAuth();
  const { planType } = usePlan();
  const { trackModuleAccess } = useAnalytics();
  const location = useLocation();
  
  // Use custom hooks for state management
  const {
    accounts,
    contacts,
    transcripts,
    industries,
    dealStages,
    loadAccounts,
    loadContacts,
    loadTranscripts,
    loadIndustries,
    loadDealStages
  } = useRadarData(user);
  
  // Local state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Navigation state
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Get current view from URL
  const getCurrentView = () => {
    const path = location.pathname;
    if (path.includes('/contacts')) return 'Contacts';
    if (path.includes('/transcripts')) return 'Transcript Analyzer';
    if (path.includes('/buyer-cards')) return 'Buyer Cards';
    return 'Accounts'; // default
  };

  const currentView = getCurrentView();

  // Load initial data
  useEffect(() => {
    const initializeData = async () => {
      try {
        setLoading(true);
        await Promise.all([
          loadAccounts(),
          loadContacts(),
          loadTranscripts(),
          loadIndustries(),
          loadDealStages()
        ]);
      } catch (err) {
        console.error('Error loading initial data:', err);
        setError('Failed to load data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      initializeData();
    }
  }, [user, loadAccounts, loadContacts, loadTranscripts, loadIndustries, loadDealStages]);

  // Track module access
  useEffect(() => {
    trackModuleAccess('radar', {
      plan: planType,
      has_accounts: accounts.length > 0,
      has_contacts: contacts.length > 0,
      has_transcripts: transcripts.length > 0
    });
  }, []);

  // Handle click outside menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  if (loading) {
    return (
      <div className="radar-container" style={{
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f8fafc'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px'
        }}>
          <FaSpinner style={{ animation: 'spin 1s linear infinite', fontSize: '40px', color: '#3b82f6' }} />
          <p style={{ color: '#6b7280', fontSize: '16px' }}>Loading Radar...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="radar-container" style={{
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f8fafc'
      }}>
        <div style={{
          background: 'white',
          padding: '24px',
          borderRadius: '8px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          textAlign: 'center'
        }}>
          <h3 style={{ color: '#dc2626', marginBottom: '12px' }}>Error</h3>
          <p style={{ color: '#6b7280', marginBottom: '16px' }}>{error}</p>
          <button
            onClick={() => window.location.reload()}
            style={{
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="radar-container" style={{
      height: '100vh',
      background: '#f8fafc',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header */}
      <RadarHeader 
        menuOpen={menuOpen}
        setMenuOpen={setMenuOpen}
        view={currentView}
        menuRef={menuRef}
      />

      {/* Main Content */}
      <div className="radar-main-content" style={{
        flex: 1,
        padding: '0 20px 20px 20px',
        overflow: 'hidden'
      }}>
        <Routes>
          <Route index element={<Navigate to="accounts" replace />} />
          <Route path="accounts" element={
            <RadarAccounts 
              contacts={contacts}
              industries={industries}
              dealStages={dealStages}
            />
          } />
          <Route path="contacts" element={
            <RadarContacts 
              accounts={accounts}
              industries={industries}
              dealStages={dealStages}
            />
          } />
          <Route path="transcripts" element={
            <RadarTranscriptAnalyzer 
              accounts={accounts}
              contacts={contacts}
              industries={industries}
              dealStages={dealStages}
            />
          } />
          <Route path="buyer-cards" element={
            <RadarBuyerCards 
              handleFeatureNotificationRequest={() => {}}
              sendingNotification={false}
              notificationSuccess=""
              notificationError=""
            />
          } />
        </Routes>
      </div>
    </div>
  );
};

export default Radar; 
